import {
  Text,
  Spinner,
  Badge,
  GreyGrey30,
  Primary13Regular,
  ShadowXs,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useMemo } from 'react'
import { localDateWithFormat, toMoney } from '@sequencehq/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import { match } from 'ts-pattern'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { useQuery } from '@sequencehq/api'
import { CreditGrantKebabMenu } from './CreditGrantKebabMenu'
import { CreditGrantsEmptyState } from './CreditGrantsEmptyState'
import { DeleteCreditGrantMenuItem } from '../menuItems/DeleteCreditGrantMenuItem'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { toCreditGrantStatusBadgeProps } from '@sequencehq/utils'

type CreditGrantUnitType = DashboardApi20240730.CreditGrants.CreditGrantUnitType

// This table reads the V1 grant list, which carries no server-derived status —
// only an expiry date. So it can tell apart just these two of the four.
type ExpiryDerivedStatus = 'ACTIVE' | 'EXPIRED'

const creditUnitTypeLabelMap: Record<CreditGrantUnitType, string> = {
  CURRENCY: 'Cash',
  METRIC: 'Units'
}

export const transformCreditGrantExpiry = (
  value: string | undefined
): ExpiryDerivedStatus => {
  if (!value) {
    return 'ACTIVE'
  }

  const dateObject = new Date(Date.parse(value))

  if (dateObject.toString() === 'Invalid Date') {
    return 'ACTIVE'
  }

  if (Date.now() < dateObject.valueOf()) {
    return 'ACTIVE'
  } else {
    return 'EXPIRED'
  }
}

export const CreditGrantTable = () => {
  const { customer } = useCustomerContext()
  const creditGrantsQuery = useQuery(
    dashboard20240730Client.getCreditGrantsForCustomer,
    {
      customerId: customer.id
    },
    { staleTime: 1 * MINUTES }
  )
  const allCreditGrantQuery = useQuery(
    dashboard20240730Client.getAllCreditGrants,
    {
      customerId: customer.id
    },
    { staleTime: 1 * MINUTES }
  )

  const creditBalances = creditGrantsQuery?.data
  const creditGrants = allCreditGrantQuery?.data

  if (!creditGrants || !creditBalances) {
    return (
      <Center height="200px" width="100%" mt={4}>
        <Spinner />
      </Center>
    )
  }
  return (
    <>
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        mt={4}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th width={120}>Status</Th>
              <Th width={175}>Balance</Th>
              <Th width={65}>Type</Th>
              <Th width={200}>Usage Metric</Th>
              <Th width={120}>Issued</Th>
              <Th width={120}>Expiry date</Th>
              <Th width={50}></Th>
            </Tr>
          </Thead>
          <Tbody>
            {creditGrants.items.length > 0 ? (
              creditGrants.items.map(creditGrant => {
                const loadedBalance = creditBalances.items.find(
                  balance => balance.id === creditGrant.id
                )

                const currentBalance =
                  loadedBalance?.balance.toString() ??
                  creditGrant.amount.toString()
                const totalAmount = creditGrant.amount.toString()

                const formatValue = (value: string) =>
                  match(creditGrant.creditUnitType)
                    .with('CURRENCY', () =>
                      toMoney({ value, currency: creditGrant.currency })
                    )
                    .with('METRIC', () => value)
                    .exhaustive()

                const balance = `${formatValue(
                  currentBalance
                )} / ${formatValue(totalAmount)}`

                return (
                  <Tr key={creditGrant.id}>
                    <Td>
                      <Text {...Primary13Regular}>{creditGrant.name}</Text>
                    </Td>
                    <Td>
                      <Flex gap={2} alignItems="center">
                        <Badge
                          {...toCreditGrantStatusBadgeProps({
                            status: transformCreditGrantExpiry(
                              creditGrant.expiryDate
                            ),
                            size: 'sm'
                          })}
                          data-testid={`creditGrant.${creditGrant.id}.status`}
                        />
                      </Flex>
                    </Td>
                    <Td>{balance}</Td>
                    <Td>
                      {creditUnitTypeLabelMap[creditGrant.creditUnitType]}
                    </Td>
                    <Td>
                      {creditGrant.metricId ? (
                        <CreditGrantUsageMetric
                          metricId={creditGrant.metricId}
                        />
                      ) : (
                        '-'
                      )}
                    </Td>
                    <Td>
                      {creditGrant.createdAt
                        ? localDateWithFormat(creditGrant.createdAt, 'dd/MM/yy')
                        : '-'}
                    </Td>
                    <Td>
                      {creditGrant.expiryDate
                        ? localDateWithFormat(
                            creditGrant.expiryDate,
                            'dd/MM/yy'
                          )
                        : 'Never'}
                    </Td>
                    <Td>
                      <Box
                        onClick={e => e.stopPropagation()}
                        data-testid={`creditGrant.${creditGrant.id}.menu`}
                      >
                        <CreditGrantKebabMenu
                          items={[
                            <DeleteCreditGrantMenuItem
                              key="delete"
                              creditGrantId={creditGrant.id}
                              creditGrantName={creditGrant.name}
                            />
                          ]}
                        />
                      </Box>
                    </Td>
                  </Tr>
                )
              })
            ) : (
              <Tr
                sx={{
                  cursor: 'auto!important',
                  _hover: {
                    bgColor: 'inherit!important'
                  }
                }}
              >
                <Td colSpan={8}>
                  <CreditGrantsEmptyState />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  )
}

const CreditGrantUsageMetric = ({ metricId }: { metricId: string }) => {
  const usageMetricQuery = useQuery(
    dashboard20240730Client.getUsageMetric,
    {
      id: metricId ?? ''
    },
    {
      enabled: !!metricId,
      staleTime: 5 * MINUTES
    }
  )

  const metricName = useMemo(() => {
    const usageMetric = usageMetricQuery.data
    return usageMetric
      ? `${usageMetric.name} (${usageMetric.aggregationType.toLowerCase()})`
      : '-'
  }, [usageMetricQuery.data])

  if (usageMetricQuery.isPending) {
    return <Skeleton width="100px" height="20px" />
  }

  return <>{metricName}</>
}
